import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const AdminCSVCountryExport = () => {
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    setExporting(true);
    try {
      // Get user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      // Fetch all countries with all fields
      const { data: countries, error } = await supabase
        .from('countries')
        .select('name, city, is_active, tagline, tourist_stay_default, remote_work_visa_option, key_requirements, tax_residency_trigger, notes_1_5_months, ease_1_5_months, ease_compliance, cost_of_living, attractiveness_rating, employee_notes, legal_compliance_risks, hr_guidelines, company_caps_observed, cost_impact_estimate, weekly_cost_excluding_flight, round_trip_flight_cost_from_france, image_url, renest_notes')
        .order('name');

      if (error) throw error;

      if (!countries || countries.length === 0) {
        toast({
          title: "Aucun pays",
          description: "Aucun pays à exporter"
        });
        return;
      }

      // Build CSV content with all fields
      const header = 'Country,City,Status,Tagline,Tourist Stay Default,Remote Work Visa,Key Requirements,Tax Residency Trigger,Notes 1-5 Months (1-5),Ease 1-5 Months (1-5),Ease Compliance (1-5),Cost of Living (1-5),Attractiveness (1-5),Employee Notes,Legal Risks,HR Guidelines,Company Caps,Impact coût (entreprise),Coût hebdo (hors vol) €,A/R depuis FR (est.) €,Image URL,Renest Notes\n';
      const rows = countries.map((country: any) => {
        const status = country.is_active ? 'Open' : 'Close';
        const escapeCSV = (val: any) => val ? `"${String(val).replace(/"/g, '""')}"` : '';
        return [
          escapeCSV(country.name),
          escapeCSV(country.city),
          status,
          escapeCSV(country.tagline),
          escapeCSV(country.tourist_stay_default),
          escapeCSV(country.remote_work_visa_option),
          escapeCSV(country.key_requirements),
          escapeCSV(country.tax_residency_trigger),
          country.notes_1_5_months || '',
          country.ease_1_5_months || '',
          country.ease_compliance || '',
          country.cost_of_living || '',
          country.attractiveness_rating || '',
          escapeCSV(country.employee_notes),
          escapeCSV(country.legal_compliance_risks),
          escapeCSV(country.hr_guidelines),
          escapeCSV(country.company_caps_observed),
          escapeCSV(country.cost_impact_estimate),
          country.weekly_cost_excluding_flight || '',
          country.round_trip_flight_cost_from_france || '',
          escapeCSV(country.image_url),
          escapeCSV(country.renest_notes)
        ].join(',');
      }).join('\n');

      const csvContent = header + rows;

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `countries_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      // Save export history
      await supabase
        .from('destination_import_export_history')
        .insert({
          company_id: '00000000-0000-0000-0000-000000000000', // Special UUID for admin actions
          admin_user_id: user.id,
          operation_type: 'admin_export',
          file_name: link.download,
          total_processed: countries.length,
          countries_opened: countries.filter((c: any) => c.is_active).length,
          countries_closed: countries.filter((c: any) => !c.is_active).length,
          errors_count: 0
        });

      toast({
        title: "Export réussi",
        description: `${countries.length} pays exportés`
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur d'export",
        description: error.message
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button onClick={handleExport} variant="outline" disabled={exporting}>
      <Download className="mr-2 h-4 w-4" />
      {exporting ? 'Export en cours...' : 'Exporter pays en CSV'}
    </Button>
  );
};

export default AdminCSVCountryExport;
